import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { StepTitle, OField, inputCls, NavButtons } from "@/components/onboard/ui";
import { MagnifyingGlass, Buildings, CheckCircle, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export function StepVerifyCompany({ token, state, onSaved }) {
  const prev = state.wizard.fmcsa || {};
  const [kind, setKind] = useState("usdot");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(prev.legal_name ? prev : null);
  const [manual, setManual] = useState(false);
  const [m, setM] = useState({ legal_name: prev.legal_name || state.carrier_name || "", usdot: prev.usdot || "", mc_number: prev.mc_number || "", address: prev.address || "" });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const search = async () => {
    if (!query.trim()) return;
    setLoading(true); setMsg(""); setResults([]);
    try {
      const { data } = await api.post(`/onboard/${token}/lookup`, { query, kind });
      if (data.ok) setResults(data.results);
      else { setMsg(data.message); setManual(true); setM((x) => ({ ...x, [kind === "mc" ? "mc_number" : "usdot"]: query })); }
    } catch (e) { setMsg(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const save = async () => {
    const body = manual ? { ...m, source: "manual" } : picked && { ...picked, source: "fmcsa" };
    if (!body || !body.legal_name) { toast.error("Select your company or enter it manually"); return; }
    setLoading(true);
    try { const { data } = await api.post(`/onboard/${token}/company-select`, body); onSaved(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <StepTitle kicker="Step 1 · Account Verification" title="Let's Find Your Company" sub="Enter your MC or USDOT number. We'll pull your company details straight from the FMCSA registry." />
      <div className="inline-flex p-1 rounded-full bg-white/5 border border-white/10 mb-4">
        {[["usdot", "USDOT #"], ["mc", "MC #"]].map(([k, l]) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`ob-kind-${k}`} className={`px-5 h-9 rounded-full text-sm font-semibold transition-colors ${kind === k ? "bg-white text-slate-900" : "text-white/60 hover:text-white"}`}>{l}</button>
        ))}
      </div>
      <div className="flex gap-3">
        <input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder={kind === "mc" ? "e.g. 1234567" : "e.g. 3456789"} className={`${inputCls} h-14 text-lg font-mono`} data-testid="ob-lookup-input" />
        <button onClick={search} disabled={loading} className="h-14 px-6 rounded-xl bg-white text-slate-900 font-semibold inline-flex items-center gap-2 hover:bg-sky-100 transition-colors shrink-0" data-testid="ob-lookup-btn"><MagnifyingGlass size={18} weight="bold" />Search</button>
      </div>
      {msg && <p className="mt-4 text-sm text-amber-300 bg-amber-400/10 border border-amber-400/20 rounded-xl px-4 py-3" data-testid="ob-lookup-msg">{msg}</p>}

      {results.length > 0 && !manual && (
        <div className="mt-6 space-y-3" data-testid="ob-results">
          {results.map((r, i) => {
            const active = picked && picked.usdot === r.usdot && picked.legal_name === r.legal_name;
            return (
              <button key={i} onClick={() => setPicked(r)} data-testid={`ob-result-${i}`} className={`w-full text-left rounded-2xl border p-5 transition-colors ${active ? "border-emerald-400/60 bg-emerald-400/[0.07]" : "border-white/10 bg-white/[0.03] hover:border-white/25"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex gap-3"><Buildings size={26} className="text-sky-300 shrink-0" />
                    <div><p className="font-heading font-bold text-white text-lg">{r.legal_name}</p>{r.dba_name && <p className="text-xs text-white/45">DBA {r.dba_name}</p>}<p className="text-sm text-white/60 mt-1">{r.address}</p>
                      <p className="text-xs text-white/45 mt-2 font-mono">USDOT {r.usdot} {r.mc_number && `· ${r.mc_number}`} {r.power_units ? `· ${r.power_units} power units` : ""}</p>
                      {r.status && <p className="text-xs mt-1.5"><span className={r.status === "Authorized" ? "text-emerald-300" : "text-red-300"}>{r.status}</span><span className="text-white/40">{r.phone ? ` · ${r.phone}` : ""}{r.safety_rating ? ` · ${r.safety_rating}` : ""}</span></p>}</div>
                  </div>
                  {active && <CheckCircle size={24} weight="fill" className="text-emerald-400 shrink-0" />}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {picked && !manual && !results.length && (
        <div className="mt-6 rounded-2xl border border-emerald-400/40 bg-emerald-400/[0.06] p-5"><p className="text-xs text-emerald-300 font-semibold uppercase tracking-wider">Selected</p><p className="font-heading font-bold text-white text-lg mt-1">{picked.legal_name}</p><p className="text-sm text-white/60">USDOT {picked.usdot} {picked.mc_number}</p></div>
      )}

      <button onClick={() => setManual((v) => !v)} className="mt-6 text-sm text-sky-300 hover:text-sky-200 inline-flex items-center gap-1.5" data-testid="ob-manual-toggle"><PencilSimple size={14} />{manual ? "Search FMCSA instead" : "Can't find it? Enter details manually"}</button>
      {manual && (
        <div className="mt-4 grid sm:grid-cols-2 gap-4" data-testid="ob-manual-form">
          <OField label="Company Legal Name *" className="sm:col-span-2"><input value={m.legal_name} onChange={(e) => setM({ ...m, legal_name: e.target.value })} className={inputCls} data-testid="ob-manual-name" /></OField>
          <OField label="USDOT #"><input value={m.usdot} onChange={(e) => setM({ ...m, usdot: e.target.value })} className={`${inputCls} font-mono`} data-testid="ob-manual-usdot" /></OField>
          <OField label="MC #"><input value={m.mc_number} onChange={(e) => setM({ ...m, mc_number: e.target.value })} className={`${inputCls} font-mono`} data-testid="ob-manual-mc" /></OField>
          <OField label="Address" className="sm:col-span-2"><input value={m.address} onChange={(e) => setM({ ...m, address: e.target.value })} className={inputCls} data-testid="ob-manual-address" /></OField>
        </div>
      )}
      <NavButtons onNext={save} loading={loading} disabled={!manual && !picked} nextLabel="This Is My Company" />
    </div>
  );
}
